import type { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "@/components/ui/use-toast";
import { limitExceededMessage, toFriendlyLimitError } from "@/lib/limitErrors";
import { cleanHandle } from "@/lib/utils/csvParsing";
import {
  formatInvalidInfluencerHandlesMessage,
  validateInfluencerHandles,
  type ValidatedInfluencerProfile,
} from "@/lib/influencerValidation";
import { BATCH_DELAY_MS, BATCH_SIZE } from "../constants";
import type { ParsedInfluencer } from "../types";

type InfluencerInsert = Database["public"]["Tables"]["influencers"]["Insert"];
type Platform = Database["public"]["Enums"]["platform_enum"];

export function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function linkInfluencersToCampaign(
  campaignId: string,
  influencerIds: string[],
  queryClient: QueryClient,
): Promise<number> {
  if (influencerIds.length === 0) return 0;

  const { data: alreadyAssigned, error: alreadyAssignedError } = await supabase
    .from("campaign_influencers")
    .select("influencer_id")
    .eq("campaign_id", campaignId)
    .in("influencer_id", influencerIds);

  if (alreadyAssignedError) {
    toast({
      title: "Failed to link influencers to campaign",
      description: alreadyAssignedError.message || "Could not check existing campaign assignments.",
      variant: "destructive",
    });
    return 0;
  }

  const alreadyAssignedIds = new Set((alreadyAssigned ?? []).map((row) => row.influencer_id));
  const toLink = influencerIds.filter((id) => !alreadyAssignedIds.has(id));
  if (toLink.length === 0) return 0;

  const { error: linkError } = await supabase.from("campaign_influencers").insert(
    toLink.map((influencerId) => ({
      campaign_id: campaignId,
      influencer_id: influencerId,
      status: "contacted" as const,
    })),
  );

  if (linkError) {
    toast({
      title: "Failed to link influencers to campaign",
      description: linkError.message || "Influencers were added but could not be linked to the campaign.",
      variant: "destructive",
    });
    return 0;
  }

  await queryClient.invalidateQueries({ queryKey: ["campaign-influencers", campaignId] });
  return toLink.length;
}

export interface ImportInfluencerParams {
  validRows: ParsedInfluencer[];
  organizationId: string;
  campaignId?: string;
  queryClient: QueryClient;
  isAtLimit: (resource: "influencers") => boolean;
  onSuccess: () => void;
}

export async function executeInfluencerImport({
  validRows,
  organizationId,
  campaignId,
  queryClient,
  isAtLimit,
  onSuccess,
}: ImportInfluencerParams): Promise<boolean> {
  if (!validRows.length) {
    toast({
      title: "No valid data to import",
      description: "Please fix validation errors first.",
      variant: "destructive",
    });
    return false;
  }

  if (!organizationId) {
    toast({
      title: "Error",
      description: "No organization selected. Please select an organization first.",
      variant: "destructive",
    });
    return false;
  }

  if (isAtLimit("influencers")) {
    toast({
      title: "Influencer limit reached",
      description: limitExceededMessage("influencers"),
      variant: "destructive",
    });
    return false;
  }

  try {
    // 1. Collect and deduplicate handles from valid CSV rows
    const allHandles = Array.from(new Set(validRows.map((r) => cleanHandle(r.handle))));
    const rowByHandle = new Map<string, ParsedInfluencer>();
    validRows.forEach((r) => {
      const cleaned = cleanHandle(r.handle);
      if (!rowByHandle.has(cleaned)) {
        rowByHandle.set(cleaned, r);
      }
    });

    // 2. Check existing handles in organization
    const { data: existingRows, error: existingError } = await supabase
      .from("influencers")
      .select("id, handle")
      .eq("organization_id", organizationId)
      .in("handle", allHandles);

    if (existingError) {
      throw new Error(existingError.message || "Could not check existing influencers.");
    }

    const existingHandleToId = new Map<string, string>();
    (existingRows ?? []).forEach((row) => {
      existingHandleToId.set(cleanHandle(row.handle), row.id);
    });

    const existingHandlesSet = new Set(existingHandleToId.keys());
    const newHandles = allHandles.filter((h) => !existingHandlesSet.has(h));
    const existingIdsToLink = campaignId ? Array.from(existingHandleToId.values()) : [];

    if (newHandles.length === 0) {
      if (campaignId && existingIdsToLink.length > 0) {
        const linkedCount = await linkInfluencersToCampaign(campaignId, existingIdsToLink, queryClient);
        toast({
          title: linkedCount > 0 ? "Influencers linked to campaign" : "No new influencers to link",
          description: linkedCount > 0
            ? `Linked ${linkedCount} existing influencer(s) to the campaign.`
            : "These influencers are already part of this campaign.",
        });
        onSuccess();
        return true;
      }

      toast({
        title: "No new influencers added",
        description: "All entered handles already exist in your organization.",
      });
      return false;
    }

    // 3. Separate Instagram handles for validation vs other platforms
    const instagramHandles: string[] = [];
    const nonInstagramHandles: string[] = [];

    newHandles.forEach((handle) => {
      const row = rowByHandle.get(handle);
      const platform = row?.platform?.toLowerCase() || "instagram";
      if (platform === "instagram") {
        instagramHandles.push(handle);
      } else {
        nonInstagramHandles.push(handle);
      }
    });

    // 4. Validate Instagram handles with validateInfluencerHandles in batches
    const profileByHandle = new Map<string, ValidatedInfluencerProfile>();
    const failedInstagramHandles: string[] = [];

    if (instagramHandles.length > 0) {
      const batches = chunk(instagramHandles, BATCH_SIZE);
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        if (i > 0) {
          await sleep(BATCH_DELAY_MS);
        }

        try {
          const validation = await validateInfluencerHandles(batch);
          validation.profiles.forEach((p) => {
            profileByHandle.set(cleanHandle(p.handle), p);
          });
          if (validation.invalidHandles.length > 0) {
            failedInstagramHandles.push(...validation.invalidHandles);
          }
        } catch (valErr) {
          console.error("Batch validation error:", valErr);
          failedInstagramHandles.push(...batch);
        }
      }
    }

    // 5. If any Instagram handles failed validation, abort and add zero influencers
    if (failedInstagramHandles.length > 0) {
      const displayHandles = failedInstagramHandles
        .map((handle) => `@${handle.replace(/^@/, '')}`)
        .join(', ');
      const message =
        failedInstagramHandles.length === 1
          ? `We couldn't find an Instagram account for ${displayHandles}. Please check the username in your CSV.`
          : `We couldn't find Instagram accounts for ${displayHandles}. Please check the usernames in your CSV.`;
      toast({
        title: "Instagram account not found",
        description: message,
        variant: "destructive",
      });
      return false;
    }

    // 6. Build payloads for strictly validated influencers only
    const influencersPayload: InfluencerInsert[] = [];

    for (const handle of instagramHandles) {
      const profile = profileByHandle.get(handle);
      if (!profile || !profile.instagramPk) {
        toast({
          title: "Profile data unavailable",
          description: `We couldn't retrieve the profile details for @${handle}, so this influencer was not added.`,
          variant: "destructive",
        });
        return false;
      }

      const row = rowByHandle.get(handle);

      influencersPayload.push({
        name: row?.name?.trim() && row.name !== row.handle ? row.name.trim() : (profile.name || handle),
        handle,
        platform: "instagram",
        organization_id: organizationId,
        created_at: new Date().toISOString(),
        enrichment_status: "completed",
        last_refreshed_at: new Date().toISOString(),
        followers_count: profile.followersCount ?? row?.followers_count ?? null,
        following_count: profile.followingCount ?? null,
        profile_pic_url: profile.profilePicUrl ?? null,
        bio: profile.bio ?? null,
        email: row?.email?.trim() || profile.email || null,
        phone: row?.phone?.trim() || null,
        location: row?.location?.trim() || null,
        business_category: profile.businessCategory ?? null,
        categories: row?.categories && row.categories.length > 0
          ? row.categories
          : (profile.businessCategory ? [profile.businessCategory] : null),
        engagement_rate: profile.engagementRate ?? row?.engagement_rate ?? null,
        avg_likes: profile.avgLikes ?? null,
        video_view_count: profile.videoViewCount ?? null,
        verified: profile.verified ?? null,
        instagram_pk: profile.instagramPk,
      });
    }

    // Non-Instagram influencers (if any)
    nonInstagramHandles.forEach((handle) => {
      const row = rowByHandle.get(handle);
      influencersPayload.push({
        name: row?.name?.trim() || handle,
        handle,
        platform: (row?.platform?.toLowerCase() || "instagram") as Platform,
        organization_id: organizationId,
        created_at: new Date().toISOString(),
        followers_count: row?.followers_count ?? null,
        engagement_rate: row?.engagement_rate ?? null,
        location: row?.location?.trim() || null,
        email: row?.email?.trim() || null,
        phone: row?.phone?.trim() || null,
        categories: row?.categories ?? null,
        enrichment_status: "pending",
      });
    });

    if (influencersPayload.length === 0) {
      toast({
        title: "Import failed",
        description: "No influencers were imported. Please check the Instagram usernames in your CSV and try again.",
        variant: "destructive",
      });
      return false;
    }

    // 7. Insert only after all influencers have been verified and enriched
    const { data: insertedData, error: insertError } = await supabase
      .from("influencers")
      .insert(influencersPayload)
      .select();

    if (insertError) {
      console.error("Import error:", insertError);
      toast({
        title: "Import failed",
        description: toFriendlyLimitError(
          insertError,
          "We couldn't import the influencers right now. Please check your file and try again."
        ),
        variant: "destructive",
      });
      return false;
    }

    // Invalidate queries
    await queryClient.invalidateQueries({ queryKey: ["influencers"] });
    await queryClient.invalidateQueries({ queryKey: ["influencers", organizationId] });

    // 8. Link to campaign if campaignId is present
    if (campaignId) {
      const newIds = (insertedData ?? []).map((inf) => inf.id);
      await linkInfluencersToCampaign(
        campaignId,
        [...newIds, ...existingIdsToLink],
        queryClient,
      );
    }

    // 9. Success feedback
    const insertedCount = insertedData?.length || 0;
    toast({
      title: "Import successful!",
      description: campaignId
        ? `Successfully validated and imported ${insertedCount} influencer(s) into the campaign.`
        : `Successfully validated and imported ${insertedCount} influencer(s).`,
    });

    onSuccess();
    return true;
  } catch (err: unknown) {
    console.error("Import error:", err);
    toast({
      title: "Import failed",
      description: toFriendlyLimitError(
        err,
        "Something went wrong while importing influencers. Please try again."
      ),
      variant: "destructive",
    });
    return false;
  }
}
